import {
  ChangeEvent,
  FocusEventHandler,
  MouseEvent,
  useState,
} from 'react'
import {
  ClearButton,
  ErrorText,
  FieldContainer,
  FieldInput,
  FieldLabel,
  FieldRoot,
} from './FloatingLabelField.styles'

export type FloatingLabelFieldProps = {
  id: string
  label: string
  value: string
  onChange: (e: ChangeEvent<HTMLInputElement>) => void
  onBlur?: FocusEventHandler<HTMLInputElement>
  onFocus?: FocusEventHandler<HTMLInputElement>
  type?: 'text' | 'email' | 'tel'
  error?: string
  required?: boolean
  autoComplete?: string
  name?: string
  disabled?: boolean
  qaAttr?: string
  className?: string
}

export const FloatingLabelField = ({
  id,
  label,
  value,
  onChange,
  onBlur,
  onFocus,
  type = 'text',
  error,
  required,
  autoComplete,
  name,
  disabled,
  qaAttr,
  className,
}: FloatingLabelFieldProps) => {
  const [focused, setFocused] = useState(false)
  const hasValue = value.length > 0
  const floated = focused || hasValue
  const hasError = Boolean(error)
  const showClear = focused && hasValue && !disabled

  const handleClear = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    const syntheticEvent = {
      target: { value: '', name: name || id },
      currentTarget: { value: '', name: name || id },
    } as unknown as ChangeEvent<HTMLInputElement>
    onChange(syntheticEvent)
  }

  return (
    <FieldRoot className={className}>
      <FieldContainer $focused={focused} $hasValue={hasValue} $hasError={hasError}>
        <FieldLabel htmlFor={id} $floated={floated} $hasError={hasError} data-floated={floated}>
          {label}
        </FieldLabel>
        <FieldInput
          id={id}
          name={name}
          type={type}
          value={value}
          onChange={onChange}
          onFocus={(e) => {
            setFocused(true)
            onFocus?.(e)
          }}
          onBlur={(e) => {
            setFocused(false)
            onBlur?.(e)
          }}
          required={required}
          autoComplete={autoComplete}
          disabled={disabled}
          data-qa={qaAttr}
          $showClear={showClear}
        />
        {showClear && (
          <ClearButton
            type="button"
            aria-label="Clear"
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleClear}
          />
        )}
      </FieldContainer>
      {error && <ErrorText>{error}</ErrorText>}
    </FieldRoot>
  )
}
